import { useEffect, useRef, useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { del, get, html, post, put, readFileBase64 } from "../lib.js";
import { Badge, Field, Loading, PageHeader, toast, useAction, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { domainErrorText, domainWarningTexts } from "../domain-messages.js";
import { LibraryCard } from "./site-library.js";

// The business website (src/sites): for a business with no site of its own, a one-page site at
// <name>.relayza.company. Claude writes the words from what setup knows; services, prices, hours
// and the address come live from the business. The owner picks the address and the look, edits
// the words, previews, and publishes. A business that already has a site can instead import its
// content into the page, or just forward its relayza.company address to the site it has.

const EMPTY = {
  lang: "he", headline: "", tagline: "", about: "", highlights: [], faq: [], cta_label: "", seo_description: "",
  credentials: [], process: [], checklist: [], calculator: false, license_no: "", disclaimer: "",
};
const EMPTY_FORM = { enabled: false, heading: "", button: "", ask_message: true, template: "", thank_you_url: "", thank_you_message: "" };
const EMPTY_PIXELS = { meta_pixel_id: "", ga4_id: "", gtm_id: "" };

export function WebsitePage({ t }) {
  const [data, reload, error] = useLoad(() => get(`${t.base}/site`), [t.id]);
  const [busy, run] = useAction();
  if (error) return html`<div class="callout error">${error.message}</div>`;
  if (!data) return html`<${Loading} />`;

  const create = async () => {
    await run(() => post(`${t.base}/site`));
    await run(() => post(`${t.base}/site/generate`), tr("Your website draft is ready"));
    reload();
  };

  return html`<div>
    <${PageHeader} title=${tr("Website")} subtitle=${tr("A one-page website for your business, with online booking and your AI chat built in.")} />
    <${PageGuide} id="website" />
    ${!data.site
      ? html`<${ExistingSiteCard} t=${t} data=${data} reload=${reload} />
        <section class="card">
          <h2>${tr("No website yet? We'll build you one")}</h2>
          <p class="muted">${tr("We write it from what you told us in setup, in a look made for your kind of business. Your services, prices and opening hours stay in sync with the agent. You check everything before it goes live, and it's included in your plan.")}</p>
          <button class="btn primary" disabled=${busy} onClick=${create}>${busy ? tr("Writing your website… this takes about half a minute") : tr("Yes, build my website")}</button>
        </section>`
      : html`<${SiteEditor} t=${t} data=${data} reload=${reload} />`}
  </div>`;
}

function SiteEditor({ t, data, reload }) {
  const site = data.site;
  const [sub, setSub] = useState(site.subdomain);
  const [subState, setSubState] = useState(null);
  const [template, setTemplate] = useState(site.template);
  const [accent, setAccent] = useState(site.accent);
  const [content, setContent] = useState({ ...EMPTY, ...data.content });
  const [leadForm, setLeadForm] = useState({ ...EMPTY_FORM, ...data.lead_form });
  const [pixels, setPixels] = useState({ ...EMPTY_PIXELS, ...data.pixels });
  const [enableBooking, setEnableBooking] = useState(!data.facts.booking_page);
  const [version, setVersion] = useState(0);
  const [busy, run] = useAction();
  const autoStarted = useRef(false);

  // A site created from setup has no words yet: write them on first open.
  useEffect(() => {
    if (data.content || autoStarted.current || site.generations_left === 0 || site.redirect_url) return;
    autoStarted.current = true;
    generate();
  }, []);

  const domainLabel = data.domain ? `.${data.domain}` : "";
  const prefix = data.domain ? "" : `${location.origin}/s/`;
  const defaultAccent = data.templates.find((x) => x.id === template)?.accent ?? "#000000";
  const set = (patch) => setContent({ ...content, ...patch });
  const setItem = (key, i, patch) => set({ [key]: content[key].map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const dropItem = (key, i) => set({ [key]: content[key].filter((_, j) => j !== i) });

  const checkSub = async () => {
    if (sub === site.subdomain) return setSubState(null);
    const res = await get(`${t.base}/site/check?subdomain=${encodeURIComponent(sub)}`);
    setSubState(res);
  };

  const save = async (message = tr("Saved")) => {
    const payload = { subdomain: sub.trim().toLowerCase(), template, accent, lead_form: leadForm, pixels };
    // Rows added but left empty are dropped rather than refused.
    if (content.headline.trim()) {
      payload.content = {
        ...content,
        credentials: content.credentials.filter((c) => c.value.trim()),
        process: content.process.filter((p) => p.title.trim()),
        checklist: content.checklist.filter((r) => r.label.trim()),
      };
    }
    await run(() => put(`${t.base}/site`, payload), message);
    setSubState(null);
    setVersion((v) => v + 1);
    reload();
  };

  async function generate() {
    const res = await run(() => post(`${t.base}/site/generate`), tr("Written. Check the words, then save."));
    setContent({ ...EMPTY, ...res.content });
    if (site.status === "draft") setVersion((v) => v + 1);
    reload();
  }

  const [publishError, setPublishError] = useState(null);
  const publish = async () => {
    setPublishError(null);
    await save(null);
    try {
      await run(() => post(`${t.base}/site/publish`, { enable_booking_page: enableBooking && !data.facts.booking_page }), tr("Your website is live"));
    } catch (err) {
      // Placeholders the AI left in [brackets] block publishing; say which fields, next to the button.
      if (err.code === "placeholders") setPublishError(err.message);
      return;
    }
    reload();
  };
  const unpublish = () => run(() => post(`${t.base}/site/unpublish`), tr("Taken offline")).then(reload);

  const preview = `/api${t.base}/site/preview?template=${template}&accent=${encodeURIComponent(accent)}&v=${version}`;
  const live = site.status === "published";
  // "I have my own website" leads the page, except once a RELAYZA page is what visitors see.
  const ownSiteFirst = !!site.redirect_url || !live;

  return html`<div class="grid-2">
    <div>
      ${ownSiteFirst && html`<${ExistingSiteCard} t=${t} data=${data} reload=${reload} />`}
      <section class="card">
        <div class="section-head">
          <h2>${tr("Address")}</h2>
          <${Badge} kind=${live ? "ok" : "neutral"}>${live ? tr("Live") : tr("Draft")}</${Badge}>
        </div>
        <div class="row" style="align-items: center; flex-wrap: nowrap">
          ${prefix && html`<span class="muted small" dir="ltr">${prefix}</span>`}
          <input dir="ltr" maxlength="40" value=${sub} onInput=${(e) => setSub(e.target.value.toLowerCase())} onBlur=${checkSub} aria-label=${tr("Website address")} />
          ${domainLabel && html`<span class="muted" dir="ltr">${domainLabel}</span>`}
        </div>
        ${subState && html`<div class=${`callout small ${subState.ok ? "ok" : "warn"}`}>${subState.ok ? tr("That name is free.") : tr(subState.problem)}</div>`}
        ${live && html`<p class="small"><a href=${site.url} target="_blank" rel="noopener" dir="ltr">${site.url}</a></p>`}
      </section>

      <section class="card">
        <h2>${tr("Look")}</h2>
        <div class="template-grid">
          ${data.templates.map((x) => html`<button type="button" class=${`template-card ${x.id === template ? "active" : ""}`} onClick=${() => setTemplate(x.id)} aria-pressed=${x.id === template}>
            <span class="template-swatch" style=${`background:${x.id === template && accent ? accent : x.accent}`}></span>
            <strong>${tr(x.label)}</strong>
            <span class="muted small">${tr(x.description)}</span>
          </button>`)}
        </div>
        <div class="row" style="align-items: center">
          <label class="toggle">${tr("Colour")} <input type="color" value=${accent || defaultAccent} onInput=${(e) => setAccent(e.target.value)} /></label>
          ${accent && html`<button type="button" class="btn small ghost" onClick=${() => setAccent("")}>${tr("Use the template's colour")}</button>`}
        </div>
      </section>

      <section class="card">
        <div class="section-head">
          <h2>${tr("Words")}</h2>
          ${site.generations_left > 0 && html`<button type="button" class="btn small ghost" disabled=${busy} onClick=${generate}>${busy ? tr("Writing…") : tr("Rewrite with AI ({n} left)", { n: site.generations_left })}</button>`}
        </div>
        <p class="muted small">${tr("Services, prices, opening hours and the address come from your business settings, so they're always current. Write only the words around them.")}</p>
        <${Field} label=${tr("Headline")}><input dir="auto" maxlength="90" value=${content.headline} onInput=${(e) => set({ headline: e.target.value })} /></${Field}>
        <${Field} label=${tr("One line under it")}><input dir="auto" maxlength="200" value=${content.tagline} onInput=${(e) => set({ tagline: e.target.value })} /></${Field}>
        <${Field} label=${tr("About us")}><textarea rows="5" dir="auto" maxlength="1500" value=${content.about} onInput=${(e) => set({ about: e.target.value })}></textarea></${Field}>
        <h3 class="small">${tr("Why choose us")}</h3>
        ${content.highlights.map((h, i) => html`<div class="draft-entry">
          <div class="row"><input dir="auto" maxlength="60" value=${h.title} onInput=${(e) => setItem("highlights", i, { title: e.target.value })} aria-label=${tr("Title")} />
            <button type="button" class="btn small ghost" onClick=${() => dropItem("highlights", i)}>${tr("Remove")}</button></div>
          <input dir="auto" maxlength="240" value=${h.text} onInput=${(e) => setItem("highlights", i, { text: e.target.value })} aria-label=${tr("Text")} />
        </div>`)}
        ${content.highlights.length < 4 && html`<button type="button" class="btn small" onClick=${() => set({ highlights: [...content.highlights, { title: "", text: "" }] })}>${tr("Add a reason")}</button>`}
        <h3 class="small">${tr("Questions")}</h3>
        ${content.faq.map((f, i) => html`<div class="draft-entry">
          <div class="row"><input dir="auto" maxlength="160" value=${f.q} onInput=${(e) => setItem("faq", i, { q: e.target.value })} aria-label=${tr("Question")} />
            <button type="button" class="btn small ghost" onClick=${() => dropItem("faq", i)}>${tr("Remove")}</button></div>
          <textarea rows="2" dir="auto" maxlength="600" value=${f.a} onInput=${(e) => setItem("faq", i, { a: e.target.value })} aria-label=${tr("Answer")}></textarea>
        </div>`)}
        ${content.faq.length < 8 && html`<button type="button" class="btn small" onClick=${() => set({ faq: [...content.faq, { q: "", a: "" }] })}>${tr("Add a question")}</button>`}
        <div class="row">
          <${Field} label=${tr("Booking button")}><input dir="auto" maxlength="30" placeholder=${tr("Book now")} value=${content.cta_label} onInput=${(e) => set({ cta_label: e.target.value })} /></${Field}>
          <${Field} label=${tr("Page language")}><select value=${content.lang} onChange=${(e) => set({ lang: e.target.value })}><option value="he">עברית</option><option value="en">English</option></select></${Field}>
        </div>
        <${Field} label=${tr("Search result description")} hint=${tr("What Google shows under your name, up to 160 characters.")}><input dir="auto" maxlength="160" value=${content.seo_description} onInput=${(e) => set({ seo_description: e.target.value })} /></${Field}>
      </section>

      ${template === "advisor" && html`<${AdvisorCard} content=${content} set=${set} setItem=${setItem} dropItem=${dropItem} />`}
      ${site.library_enabled && t.role !== "agent" && html`<${LibraryCard} t=${t} />`}

      <${LeadFormCard} data=${data} form=${leadForm} setForm=${setLeadForm} />
      <${ShareCard} t=${t} data=${data} reload=${reload} advisor=${template === "advisor"} onChange=${() => setVersion((v) => v + 1)} />
      <${PixelsCard} pixels=${pixels} setPixels=${setPixels} />
      <${DomainCard} t=${t} data=${data} reload=${reload} />
      ${!ownSiteFirst && html`<${ExistingSiteCard} t=${t} data=${data} reload=${reload} />`}

      <section class="card">
        <h2>${tr("Before you publish")}</h2>
        <ul class="plain small">
          <li>${data.facts.services ? "✓" : "•"} ${data.facts.services ? tr("{n} services with their prices", { n: data.facts.services }) : html`${tr("No services yet.")} <a href="/services">${tr("Add them")}</a>`}</li>
          <li>${data.facts.has_hours ? "✓" : "•"} ${data.facts.has_hours ? tr("Opening hours") : html`${tr("No opening hours yet.")} <a href="/services">${tr("Set them")}</a>`}</li>
          <li>${data.facts.has_address ? "✓" : "•"} ${data.facts.has_address ? tr("Address") : html`${tr("No address yet.")} <a href="/settings">${tr("Add it")}</a>`}</li>
          <li>${data.facts.chat ? "✓ " + tr("Your AI chat answers visitors") : "• " + tr("Connect the website chat under Channels to answer visitors on the site.")}</li>
        </ul>
        ${!data.facts.booking_page && html`<label class="toggle"><input type="checkbox" checked=${enableBooking} onChange=${(e) => setEnableBooking(e.target.checked)} /> ${tr("Turn on online booking when I publish, so the button books")}</label>`}
        ${publishError && html`<div class="callout small warn" role="alert">${publishError}</div>`}
        <div class="wizard-actions">
          <button type="button" class="btn primary" disabled=${busy || !content.headline.trim()} onClick=${publish}>${live ? tr("Save and update the live site") : tr("Publish my website")}</button>
          ${!live && html`<button type="button" class="btn" disabled=${busy} onClick=${() => save()}>${tr("Save draft")}</button>`}
          ${live && html`<button type="button" class="btn ghost" disabled=${busy} onClick=${unpublish}>${tr("Take offline")}</button>`}
        </div>
      </section>
    </div>

    <section class="card site-preview">
      <div class="section-head"><h2>${tr("Preview")}</h2><span class="muted small">${tr("Save to see your latest words")}</span></div>
      ${data.content ? html`<iframe title=${tr("Website preview")} src=${preview} loading="lazy"></iframe>` : html`<${Loading} />`}
    </section>
  </div>`;
}

/** The advisor template's own sections; each shows on the page only when it has something in it. */
function AdvisorCard({ content, set, setItem, dropItem }) {
  const statuses = [["warn", tr("Worth checking")], ["ok", tr("Fine")], ["info", tr("To update")]];
  return html`<section class="card">
    <h2>${tr("Practice details")}</h2>
    <p class="muted small">${tr("Sections of the Advisor look. Only state what is true: credentials, licence numbers and figures are yours to give.")}</p>
    <h3 class="small">${tr("Credentials")}</h3>
    ${content.credentials.map((c, i) => html`<div class="row">
      <input dir="auto" maxlength="24" value=${c.value} onInput=${(e) => setItem("credentials", i, { value: e.target.value })} aria-label=${tr("Value, e.g. 20 or MBA")} placeholder=${tr("Value, e.g. 20 or MBA")} />
      <input dir="auto" maxlength="80" value=${c.label} onInput=${(e) => setItem("credentials", i, { label: e.target.value })} aria-label=${tr("What it means")} placeholder=${tr("What it means")} />
      <button type="button" class="btn small ghost" onClick=${() => dropItem("credentials", i)}>${tr("Remove")}</button>
    </div>`)}
    ${content.credentials.length < 4 && html`<button type="button" class="btn small" onClick=${() => set({ credentials: [...content.credentials, { value: "", label: "" }] })}>${tr("Add a credential")}</button>`}
    <h3 class="small">${tr("How it works")}</h3>
    ${content.process.map((p, i) => html`<div class="draft-entry">
      <div class="row"><span class="muted">${i + 1}.</span><input dir="auto" maxlength="60" value=${p.title} onInput=${(e) => setItem("process", i, { title: e.target.value })} aria-label=${tr("Step")} />
        <button type="button" class="btn small ghost" onClick=${() => dropItem("process", i)}>${tr("Remove")}</button></div>
      <input dir="auto" maxlength="240" value=${p.text} onInput=${(e) => setItem("process", i, { text: e.target.value })} aria-label=${tr("Text")} />
    </div>`)}
    ${content.process.length < 6 && html`<button type="button" class="btn small" onClick=${() => set({ process: [...content.process, { title: "", text: "" }] })}>${tr("Add a step")}</button>`}
    <h3 class="small">${tr("What we check (example card)")}</h3>
    ${content.checklist.map((r, i) => html`<div class="draft-entry">
      <div class="row"><input dir="auto" maxlength="60" value=${r.label} onInput=${(e) => setItem("checklist", i, { label: e.target.value })} aria-label=${tr("What is checked")} />
        <select value=${r.status} onChange=${(e) => setItem("checklist", i, { status: e.target.value })} aria-label=${tr("Status")}>${statuses.map(([v, l]) => html`<option value=${v}>${l}</option>`)}</select>
        <button type="button" class="btn small ghost" onClick=${() => dropItem("checklist", i)}>${tr("Remove")}</button></div>
      <input dir="auto" maxlength="160" value=${r.note} onInput=${(e) => setItem("checklist", i, { note: e.target.value })} aria-label=${tr("Note")} placeholder=${tr("Note")} />
    </div>`)}
    ${content.checklist.length < 5 && html`<button type="button" class="btn small" onClick=${() => set({ checklist: [...content.checklist, { label: "", note: "", status: "info", chip: "" }] })}>${tr("Add a check")}</button>`}
    <label class="toggle"><input type="checkbox" checked=${content.calculator} onChange=${(e) => set({ calculator: e.target.checked })} /> ${tr("Show the retirement calculator")}</label>
    <div class="row">
      <${Field} label=${tr("Licence number")}><input dir="ltr" maxlength="60" value=${content.license_no} onInput=${(e) => set({ license_no: e.target.value })} /></${Field}>
    </div>
    <${Field} label=${tr("Footer notice")} hint=${tr("The regulatory notice under your site, e.g. that the content isn't personal advice.")}><textarea rows="3" dir="auto" maxlength="800" value=${content.disclaimer} onInput=${(e) => set({ disclaimer: e.target.value })}></textarea></${Field}>
  </section>`;
}

/** "Leave your number": a sent form becomes a WhatsApp contact the agent writes to first. */
function LeadFormCard({ data, form, setForm }) {
  const set = (patch) => setForm({ ...form, ...patch });
  const site = data.site;
  return html`<section class="card">
    <div class="section-head">
      <h2>${tr("Contact form")}</h2>
      ${site.leads_count > 0 && html`<${Badge} kind="ok">${tr("{n} sent", { n: site.leads_count })}</${Badge}>`}
    </div>
    <p class="muted small">${tr("Visitors leave a name and number. They become a contact with the ad or campaign that brought them, the AI agent writes to them on WhatsApp within seconds, and your team gets an alert.")}</p>
    ${!data.facts.whatsapp && html`<div class="callout small warn">${tr("Connect WhatsApp under Channels first: the form only shows once there's a number to write from.")}</div>`}
    <label class="toggle"><input type="checkbox" checked=${form.enabled} onChange=${(e) => set({ enabled: e.target.checked })} /> ${tr("Show a contact form on my website")}</label>
    ${form.enabled && html`
      <div class="row">
        <${Field} label=${tr("Heading")}><input dir="auto" maxlength="80" placeholder=${tr("Want us to get back to you?")} value=${form.heading} onInput=${(e) => set({ heading: e.target.value })} /></${Field}>
        <${Field} label=${tr("Button")}><input dir="auto" maxlength="30" placeholder=${tr("Send")} value=${form.button} onInput=${(e) => set({ button: e.target.value })} /></${Field}>
      </div>
      <label class="toggle"><input type="checkbox" checked=${form.ask_message} onChange=${(e) => set({ ask_message: e.target.checked })} /> ${tr("Also ask what they need help with")}</label>
      <${Field} label=${tr("WhatsApp template for the first message")} hint=${tr("A new contact hasn't written to you, so WhatsApp only allows an approved template. Body: {{1}} is their name. Without one, the lead waits as a task for a person to call.")}>
        <input dir="ltr" maxlength="512" placeholder="website_lead_hello" value=${form.template} onInput=${(e) => set({ template: e.target.value.trim().toLowerCase() })} />
      </${Field}>
      <${Field} label=${tr("After sending, go to (optional)")} hint=${tr("A thank-you page of your own, starting with https://. Empty: a thank-you line on the same page.")}>
        <input dir="ltr" maxlength="500" placeholder="https://" value=${form.thank_you_url} onInput=${(e) => set({ thank_you_url: e.target.value.trim() })} />
      </${Field}>
      <${Field} label=${tr("Thank-you message (optional)")} hint=${tr("Shown after a successful submission when no redirect URL is configured. Leave blank for the default message.")}>
        <textarea dir="auto" maxlength="240" rows="2" value=${form.thank_you_message || ""} onInput=${(e) => set({ thank_you_message: e.target.value })}></textarea>
      </${Field}>
      <p class="muted small">${tr("The form asks for consent to updates separately, unticked: only people who tick it can get your campaigns.")}</p>`}
  </section>`;
}

/** The share image (what WhatsApp and Facebook show under a link) and the favicon. */
function ShareCard({ t, data, reload, advisor, onChange }) {
  const [busy, run] = useAction();
  const assets = data.site.assets ?? {};
  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const b64 = await readFileBase64(file);
    await run(() => put(`${t.base}/site/assets/${kind}`, { data_url: `data:${file.type};base64,${b64}` }), tr("Uploaded"));
    onChange();
    reload();
  };
  const remove = (kind) => async () => {
    await run(() => del(`${t.base}/site/assets/${kind}`), tr("Removed"));
    onChange();
    reload();
  };
  const src = (kind) => `/api${t.base}/site/_assets/${kind}?v=${encodeURIComponent(assets[kind] ?? "default")}`;
  return html`<section class="card">
    <h2>${tr("Share preview and icon")}</h2>
    <p class="muted small">${tr("When someone shares your site on WhatsApp or Facebook, the share image appears under the link. The icon shows in the browser tab.")}</p>
    <div class="row" style="align-items: flex-start">
      <div>
        <h3 class="small">${tr("Share image")}</h3>
        ${assets.og ? html`<img src=${src("og")} alt="" style="max-width: 240px; border-radius: 8px; display: block" />` : html`<p class="muted small">${tr("None yet. 1200×630 pixels, JPEG or PNG, under 300 KB shows best on WhatsApp.")}</p>`}
        <div class="row">
          <label class="btn small">${assets.og ? tr("Replace") : tr("Upload")}<input type="file" accept="image/jpeg,image/png" hidden disabled=${busy} onChange=${upload("og")} /></label>
          ${assets.og && html`<button type="button" class="btn small ghost" disabled=${busy} onClick=${remove("og")}>${tr("Remove")}</button>`}
        </div>
      </div>
      <div>
        <h3 class="small">${tr("Icon")}</h3>
        <img src=${src("icon")} alt="" width="48" height="48" style="border-radius: 8px; display: block" />
        ${!assets.icon && html`<p class="muted small">${tr("Until you upload one, it's your first letter on your colour.")}</p>`}
        <div class="row">
          <label class="btn small">${assets.icon ? tr("Replace") : tr("Upload")}<input type="file" accept="image/png,.ico" hidden disabled=${busy} onChange=${upload("icon")} /></label>
          ${assets.icon && html`<button type="button" class="btn small ghost" disabled=${busy} onClick=${remove("icon")}>${tr("Remove")}</button>`}
        </div>
      </div>
    </div>
    ${advisor && html`<div class="row" style="align-items: flex-start">
      ${[["logo", tr("Logo"), tr("Shown in the header. Until you upload one, the icon is.")], ["portrait", tr("Portrait"), tr("Shown beside About us.")]].map(
        ([kind, label, hint]) => html`<div>
          <h3 class="small">${label}</h3>
          ${assets[kind] ? html`<img src=${src(kind)} alt="" style="max-width: 120px; border-radius: 8px; display: block" />` : html`<p class="muted small">${hint}</p>`}
          <div class="row">
            <label class="btn small">${assets[kind] ? tr("Replace") : tr("Upload")}<input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled=${busy} onChange=${upload(kind)} /></label>
            ${assets[kind] && html`<button type="button" class="btn small ghost" disabled=${busy} onClick=${remove(kind)}>${tr("Remove")}</button>`}
          </div>
        </div>`,
      )}
    </div>
    <p class="muted small">${tr("PNG, JPEG or WebP, under 300 KB.")}</p>`}
  </section>`;
}

/** Ad and analytics tags by id; the page writes each vendor's standard snippet. */
function PixelsCard({ pixels, setPixels }) {
  const set = (patch) => setPixels({ ...pixels, ...patch });
  return html`<section class="card">
    <h2>${tr("Ads and analytics tracking")}</h2>
    <p class="muted small">${tr("Paste only the id; we add the standard code. A sent contact form counts as a Lead in Meta and generate_lead in Google.")}</p>
    <${Field} label="Meta Pixel"><input dir="ltr" maxlength="20" placeholder="123456789012345" value=${pixels.meta_pixel_id} onInput=${(e) => set({ meta_pixel_id: e.target.value.trim() })} /></${Field}>
    <div class="row">
      <${Field} label="Google Analytics 4"><input dir="ltr" maxlength="20" placeholder="G-XXXXXXX" value=${pixels.ga4_id} onInput=${(e) => set({ ga4_id: e.target.value.trim().toUpperCase() })} /></${Field}>
      <${Field} label="Google Tag Manager"><input dir="ltr" maxlength="20" placeholder="GTM-XXXXXX" value=${pixels.gtm_id} onInput=${(e) => set({ gtm_id: e.target.value.trim().toUpperCase() })} /></${Field}>
    </div>
    <p class="muted small">${tr("These tags set cookies and send visits to Meta or Google. Say so in your privacy notice, as Israeli privacy law expects.")}</p>
  </section>`;
}

/** The business's own domain, through Cloudflare for SaaS. */
function DomainCard({ t, data, reload }) {
  const [busy, run] = useAction();
  const current = data.site.custom_domain;
  const [domain, setDomain] = useState("");
  const [connecting, setConnecting] = useState(false);
  // Shown in the card, not a toast: both are worth reading before touching DNS.
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState(null);
  if (!data.domains_enabled) return null;
  const connect = async () => {
    setConnecting(true);
    setError(null);
    setWarnings(null);
    try {
      const res = await post(`${t.base}/site/domain`, { domain });
      const host = res.site?.custom_domain?.host ?? domain.trim();
      setWarnings(domainWarningTexts(host, res.domain_warnings));
      toast(tr("Domain added. Now add the record below at your domain provider."));
      setDomain("");
      reload();
    } catch (err) {
      // The server's own words are English; the code says it in the owner's language.
      setError(domainErrorText(err));
    } finally {
      setConnecting(false);
    }
  };
  // Hosts and Cloudflare's reason are left-to-right inside a Hebrew sentence.
  const say = ([text, vars]) => tr(text, Object.fromEntries(Object.entries(vars).map(([k, v]) => [k, v ? `\u2066${v}\u2069` : v])));
  const check = () => run(() => post(`${t.base}/site/domain/check`), tr("Checked")).then(reload);
  const remove = () => run(() => del(`${t.base}/site/domain`), tr("Domain removed")).then(reload);
  const status = current && { active: ["ok", tr("Connected")], pending: ["warn", tr("Waiting for DNS")], failed: ["muted", tr("Not connected")] }[current.status];
  return html`<section class="card">
    <div class="section-head">
      <h2>${tr("Your own domain")}</h2>
      ${status && html`<${Badge} kind=${status[0]}>${status[1]}</${Badge}>`}
    </div>
    ${!current
      ? html`<p class="muted small">${tr("Already own a domain? Point an address on it, like www or book, at this website. Your relayza.company address keeps working too.")}</p>
          <div class="row" style="align-items: center; flex-wrap: nowrap">
            <input dir="ltr" maxlength="253" placeholder="www.your-business.co.il" value=${domain} onInput=${(e) => setDomain(e.target.value)} aria-label=${tr("Your domain")} />
            <button type="button" class="btn" disabled=${busy || connecting || domain.trim().length < 4} onClick=${connect}>${tr("Connect")}</button>
          </div>
          ${error && html`<div class="callout small error" role="alert">${error.map((line) => html`<p>${say(line)}</p>`)}</div>`}
          <p class="muted small">${tr("Use an address with something before your domain (www.example.co.il). A bare example.co.il works only where your DNS provider supports CNAME flattening.")}</p>`
      : html`<p dir="ltr"><strong>${current.host}</strong></p>
          ${warnings?.length > 0 && html`<div class="callout small warn" role="status">${warnings.map((line) => html`<p>${say(line)}</p>`)}</div>`}
          ${current.status !== "active" && current.records.length > 0 && html`
            <p class="small">${tr("At the company you bought the domain from, add this DNS record. It usually takes effect within an hour.")}</p>
            <table class="table small" dir="ltr">
              <thead><tr><th>${tr("Type")}</th><th>${tr("Name")}</th><th>${tr("Value")}</th></tr></thead>
              <tbody>${current.records.map((r) => html`<tr><td>${r.type}</td><td><code>${r.name}</code></td><td><code>${r.value}</code>${!r.required ? html` <span class="muted">(${tr("optional, speeds up verification")})</span>` : ""}</td></tr>`)}</tbody>
            </table>`}
          ${current.errors.length > 0 && html`<div class="callout small warn">${current.errors.join(" · ")}</div>`}
          ${current.status === "active" && html`<p class="small">${tr("Visitors and search engines now get your site at this address.")}</p>`}
          <div class="row">
            ${current.status !== "active" && html`<button type="button" class="btn small" disabled=${busy} onClick=${check}>${tr("Check now")}</button>`}
            <button type="button" class="btn small ghost" disabled=${busy} onClick=${remove}>${tr("Remove domain")}</button>
          </div>`}
  </section>`;
}

/**
 * A website the business already has: bring its content into the RELAYZA page, or forward the
 * RELAYZA address to it. Either only after the owner confirms the site is theirs.
 */
function ExistingSiteCard({ t, data, reload }) {
  const [busy, run] = useAction();
  const site = data.site;
  // The site the business already gave in setup or Settings is theirs by their own word: it comes
  // filled in and confirmed, so connecting is one press. Typing another address asks again.
  const known = data.existing_website || "";
  const [url, setUrl] = useState(site?.redirect_url || site?.source_url || known);
  const [mine, setMine] = useState(!!known && url === known);
  const ready = mine && url.trim().length > 3;
  const prefilled = !!known && url === known;

  if (site?.redirect_url) {
    const stop = () => run(() => put(`${t.base}/site/redirect`, { url: "" }), tr("Forwarding stopped")).then(reload);
    return html`<section class="card">
      <div class="section-head"><h2>${tr("Forwarding to your website")}</h2><${Badge} kind="ok">${tr("Live")}</${Badge}></div>
      <p class="small">${tr("Visitors to your relayza.company address go straight to:")} <a href=${site.redirect_url} target="_blank" rel="noopener" dir="ltr">${site.redirect_url}</a></p>
      <p class="muted small">${tr("To show a RELAYZA page there instead, stop forwarding and write the page below.")}</p>
      ${site.library_enabled && html`<p class="muted small">${tr("Your lecture and file pages and their QR codes keep working on your relayza.company address.")}</p>`}
      <button type="button" class="btn small ghost" disabled=${busy} onClick=${stop}>${tr("Stop forwarding")}</button>
    </section>`;
  }

  const importIt = async () => {
    await run(() => post(`${t.base}/site/import`, { url, owner_confirmed: true }), tr("Imported. Check the words, then save."));
    reload();
  };
  const forward = async () => {
    await run(() => put(`${t.base}/site/redirect`, { url, owner_confirmed: true }), tr("Your address now forwards to your website"));
    reload();
  };
  return html`<section class="card">
    <h2>${tr("I have my own website")}</h2>
    <p class="muted small">${tr("Bring its content into a RELAYZA page with booking and chat built in, or just forward your relayza.company address to it.")}</p>
    <${Field} label=${tr("Your website")} hint=${prefilled ? tr("From your business details. Check it's right, then connect.") : null}>
      <input dir="ltr" maxlength="500" placeholder="https://" value=${url} onInput=${(e) => { setUrl(e.target.value); if (e.target.value !== known) setMine(false); }} />
    </${Field}>
    <label class="toggle small"><input type="checkbox" checked=${mine} onChange=${(e) => setMine(e.target.checked)} /> ${tr("This website belongs to my business")}</label>
    <div class="row">
      ${prefilled
        ? html`<button type="button" class="btn primary" disabled=${busy || !ready} onClick=${forward}>${tr("Connect my website")}</button>
            <button type="button" class="btn" disabled=${busy || !ready} onClick=${importIt}>${busy ? tr("Reading your website… this takes about half a minute") : tr("Import its content")}</button>`
        : html`<button type="button" class="btn primary" disabled=${busy || !ready} onClick=${importIt}>${busy ? tr("Reading your website… this takes about half a minute") : tr("Import its content")}</button>
            <button type="button" class="btn" disabled=${busy || !ready} onClick=${forward}>${tr("Just forward to it")}</button>`}
    </div>
    <p class="muted small">${tr("Importing writes fresh words from your site's facts in a RELAYZA look; it doesn't copy the design. Forwarding keeps your site exactly as it is, without RELAYZA's booking and chat on it.")}</p>
  </section>`;
}
